<template>
    <card-collapsable title="Alertas anteriores">
        <b-row align-h="center">
            <b-col md="8">
                <date-range-selector
                    ref="dateSelector"
                    label="Intervalo de tempo"
                    size="md"
                />
            </b-col>
        </b-row>
        <b-row align-h="center">
            <b-col md="8">
                <b-form-group label="Categoria">
                    <b-form-checkbox-group
                        id="checkbox-group-alert-category"
                        v-model="alertCategory"
                        :options="alertCategories"
                        name="alert_category"
                        switches
                    />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row align-h="center">
            <b-col md="8">
                <b-form-group label="Tipo">
                    <b-form-checkbox-group
                        id="checkbox-group-alert-type"
                        v-model="alertType"
                        :options="alertTypes"
                        name="alert_type"
                        switches
                    />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row align-h="center">
            <b-col md="8">
                <b-form-group label="Nível">
                    <b-form-checkbox-group
                        id="checkbox-group-alert-level"
                        v-model="alertLevel"
                        :options="alertLevels"
                        name="alert_level"
                        switches
                    />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row align-h="center">
            <b-col md="8">
                <b-form-group
                    id="title_group_id"
                    label="Título"
                    label-for="title_id"
                    description="Termo a ser buscado no título do alerta (opcional)."
                >
                    <b-form-input
                        id="title_id"
                        v-model="title"
                        required
                        placeholder="Título..."
                    />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row align-h="center">
            <b-col md="8">
                <b-form-group
                    id="description_group_id"
                    label="Descrição"
                    label-for="description_id"
                    description="Termo a ser buscado na descrição do alerta (opcional)."
                >
                    <b-form-input
                        id="description_id"
                        v-model="description"
                        required
                        placeholder="Descrição..."
                    />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row align-h="center">
            <b-col md="8" class="text-center mb-3">
                <b-overlay
                    :show="loadingSearch"
                    class="d-inline-block"
                    variant="white"
                    :rounded="true"
                    spinner-small
                >
                    <b-button
                        class="pl-1"
                        :disabled="loadingSearch"
                        @click="search(1, 'clear')"
                    >
                        <i class="fas fa-fw fa-sm fa-search" />
                        Buscar alertas
                    </b-button>
                </b-overlay>
            </b-col>
        </b-row>
        <b-row align-h="center">
            <b-col cols="12">
                <alert-table :items="results" :busy="loading" />
                <b-pagination
                    :value="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    :disabled="loading"
                    first-text="Primeira página"
                    last-text="Última página"
                    align="center"
                    @change="search($event, 'use')"
                />
            </b-col>
        </b-row>
    </card-collapsable>
</template>

<script>
import { mapState } from "vuex";
import sleep from "@/utils/sleep";
import api from "@/modules/common/api";
import DateRangeSelector from "@/components/DateRangeSelector";
import CardCollapsable from "@/components/CardCollapsable";
import {
    AlertLevels,
    AlertTypes,
    AlertCategories
} from "@/modules/common/settings";
import AlertTable from "./AlertTable";

export default {
    name: "AlertPrev",
    components: {
        CardCollapsable,
        DateRangeSelector,
        AlertTable
    },
    data() {
        return {
            loading: false,
            loadingSearch: false,
            alertCategory: [AlertCategories.STRUCTURE, AlertCategories.SYSTEM],
            alertType: [
                AlertTypes.RESERVOIR,
                AlertTypes.RTS,
                AlertTypes.SENSORS
            ],
            alertLevel: [
                AlertLevels.CRITICAL,
                AlertLevels.WARNING,
                AlertLevels.INFO
            ],
            title: "",
            description: "",
            currentPage: 1,
            results: [],
            totalRows: 0,
            cache: {}
        };
    },
    computed: {
        ...mapState("common", ["source"])
    },
    created() {
        this.perPage = 5;
        this.alertTypes = [
            {
                text: "Reservatório",
                value: AlertTypes.RESERVOIR
            },
            {
                text: "RTS",
                value: AlertTypes.RTS
            },
            {
                text: "Sensores",
                value: AlertTypes.SENSORS
            }
        ];
        this.alertLevels = [
            {
                text: "Crítico",
                value: AlertLevels.CRITICAL
            },
            {
                text: "Aviso",
                value: AlertLevels.WARNING
            },
            {
                text: "Informação",
                value: AlertLevels.INFO
            }
        ];
        this.alertCategories = [
            {
                text: AlertCategories[AlertCategories.STRUCTURE],
                value: AlertCategories.STRUCTURE
            },
            {
                text: AlertCategories[AlertCategories.SYSTEM],
                value: AlertCategories.SYSTEM
            }
        ];
        this.currentParams = {};
    },
    methods: {
        async search(currentPage, cacheAction) {
            if (cacheAction === "clear") {
                this.cache = {};
                this.loadingSearch = true;
                this.currentParams = this.getParams();
            } else if (currentPage in this.cache) {
                this.paginate(this.cache[currentPage]);
                return;
            }
            this.currentParams.currentPage = currentPage;

            const data = await this.getData();
            this.cache[data.current_page] = data;
            this.paginate(data);
        },
        paginate(data) {
            this.currentPage = data.current_page;
            this.results = data.items;
            this.totalRows = data.total_rows;
        },
        getParams() {
            const description = this.description.trim();
            const title = this.title.trim();
            const { startDate, endDate } = this.$refs.dateSelector.value;
            const params = {
                perPage: this.perPage,
                startDate,
                endDate
            };

            if (title !== "") {
                params.title = title;
            }

            if (description !== "") {
                params.description = description;
            }

            if (this.alertType.length > 0) {
                params.alertType = this.alertType;
            }

            if (this.alertLevel.length > 0) {
                params.alertLevel = this.alertLevel;
            }

            if (this.alertCategory.length > 0) {
                params.alertCategory = this.alertCategory;
            }

            return params;
        },
        async getData() {
            this.loading = true;

            await sleep(500);

            const { data } = await api.getAlerts(this.source, {
                ...this.currentParams
            });

            this.loading = false;

            if (this.loadingSearch) {
                this.loadingSearch = false;
            }

            return data;
        }
    }
};
</script>
